import React, { useEffect, useCallback } from 'react';
import {useTranslation} from 'react-i18next';
import {MapContainer, TileLayer, ZoomControl, useMapEvents, useMap} from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import 'leaflet.locatecontrol/dist/L.Control.Locate.min.css'
import TrafficLightMarkers from './markers/TrafficLightMarkers';
import LocateControl from "./controls/LocateControl";
import { TrafficInfra } from "../trafficInfra/TrafficInfra";
import AppTitle from "./ui/AppTitle";
import { TunnelState } from "../tunnelState/TunnelState";
import TunnelMarkers from "./markers/TunnelMarkers";
import RoutingMachine from "./logic/RoutingMachine";
import TrafficLightHUD from "./ui/TrafficLightHUD";
import { useSelector, useDispatch } from 'react-redux';

import ControlContainer from "./ui/ControlContainer";
import LanguageSwitcher from "./ui/LanguageSwitcher";
import NavigationActionButton from "./ui/NavigationActionButton";
import DebugControls from "./debug/DebugControls";
import "./logic/LeafletSetup";
import styles from './Map.module.css';
import { findActiveTrafficLight } from "./logic/NavigationHelpers";
import { useGPSTracking } from "./hooks/useGPSTracking";
import { UserMarker } from "./markers/UserMarker";
import {
    setRouteCoordinates,
    setIsRoutingMode,
    setIsDrivingMode,
    setActiveLight,
    setActiveLightDistance,
    setIsHudVisible,
    setWaypoints,
    setRouteProximity,
    setHudDistanceThreshold,
    setHudTimeThreshold,
    selectCurrentLocation,
    selectZoom,
    selectRouteCoordinates,
    selectIsRoutingMode,
    selectIsDrivingMode,
    selectCurrentBearing,
    selectCurrentSpeed,
    selectActiveLight,
    selectActiveLightDistance,
    selectIsHudVisible,
    selectWaypoints,
    selectRouteProximity,
    selectHudDistanceThreshold,
    selectHudTimeThreshold
} from './mapSlice';
import { selectIntersections } from "../trafficInfra/trafficInfraSlice";


// Component to handle map clicks
const MapClickHandler = ({ onClick }) => {
    useMapEvents({
        click: (e) => {
            onClick(e.latlng);
        },
    });
    return null;
};

// Component to handle map cursor
const MapCursor = ({ isRoutingMode, waypoints }) => {
    const map = useMapEvents({});
    useEffect(() => {
        const container = map.getContainer();
        // Reset classes first
        container.classList.remove('cursor-start', 'cursor-end');

        if (isRoutingMode) {
            if (waypoints.length === 0) {
                container.classList.add('cursor-start');
            } else if (waypoints.length === 1) {
                container.classList.add('cursor-end');
            }
        }
    }, [map, isRoutingMode, waypoints]);
    return null;
};


// Helper to center map on user location updates
const MapCentering = ({ center, isDrivingMode }) => {
    const map = useMap();
    useEffect(() => {
        if (isDrivingMode && center) {
            map.setView(center, map.getZoom(), { animate: true });
        }
    }, [center, isDrivingMode, map]);
    return null;
};

const MapView = () => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const intersections = useSelector(selectIntersections);

    const currentLocation = useSelector(selectCurrentLocation);
    const zoom = useSelector(selectZoom);
    const routeCoordinates = useSelector(selectRouteCoordinates);
    const isRoutingMode = useSelector(selectIsRoutingMode);
    const isDrivingMode = useSelector(selectIsDrivingMode);
    const currentBearing = useSelector(selectCurrentBearing);
    const currentSpeed = useSelector(selectCurrentSpeed);
    const activeLight = useSelector(selectActiveLight);
    const activeLightDistance = useSelector(selectActiveLightDistance);
    const isHudVisible = useSelector(selectIsHudVisible);
    const waypoints = useSelector(selectWaypoints);
    const routeProximity = useSelector(selectRouteProximity);
    const hudDistanceThreshold = useSelector(selectHudDistanceThreshold);
    const hudTimeThreshold = useSelector(selectHudTimeThreshold);

    // Filter intersections to only those on the route
    const relevantIntersections = React.useMemo(() => {
        if (!routeCoordinates || !intersections?.features) return intersections;

        const filteredFeatures = intersections.features.filter(feature => {
            const { geometry } = feature;
            if (!geometry) return false;
            const { coordinates } = geometry;
            // coordinates are [lng, lat] in GeoJSON
            const markerLatLng = L.latLng(coordinates[0][1], coordinates[0][0]);

            // Check if marker is close to any point on the route
            return routeCoordinates.some(coord => markerLatLng.distanceTo(coord) < routeProximity);
        });

        return { ...intersections, features: filteredFeatures };
    }, [routeCoordinates, intersections, routeProximity]);

    const checkProximity = (location, bearing) => {
        // Use a large search radius to find the light even if far away (for debug info)
        const SEARCH_RADIUS = 2000;

        const { activeLight, distance } = findActiveTrafficLight(
            location,
            relevantIntersections,
            bearing,
            SEARCH_RADIUS,
            45 // Approach angle (degrees)
        );

        // Ensure we only store serializable data
        dispatch(setActiveLight(activeLight));
        dispatch(setActiveLightDistance(distance));

        // Determine if HUD should be visible
        if (activeLight) {
            const timeToLight = currentSpeed > 0 ? distance / (currentSpeed / 3.6) : Infinity;
            const shouldShow = distance <= hudDistanceThreshold || timeToLight <= hudTimeThreshold;
            dispatch(setIsHudVisible(shouldShow));
        } else {
            dispatch(setIsHudVisible(false));
        }
    };

    const handleRouteFound = useCallback((coordinates) => {
        dispatch(setRouteCoordinates(coordinates));
    }, [dispatch]);

    const toggleRoutingMode = () => {
        dispatch(setIsRoutingMode(!isRoutingMode));
        dispatch(setWaypoints([]));
        dispatch(setRouteCoordinates(null));
    };

    // Use GPS tracking for navigation (can be mocked via console monkey patching)
    useGPSTracking(checkProximity);

    const toggleDrivingMode = () => {
        dispatch(setIsDrivingMode(!isDrivingMode));
    };

    const handleMapClick = (latlng) => {
        if (!isRoutingMode) return;

        if (waypoints.length < 2) {
            dispatch(setWaypoints([...waypoints, { lat: latlng.lat, lng: latlng.lng }]));
        }
    };

    const handleProximityChange = (e) => {
        dispatch(setRouteProximity(parseInt(e.target.value, 10)));
    };

    const handleHudDistanceChange = (e) => {
        dispatch(setHudDistanceThreshold(parseInt(e.target.value, 10)));
    };

    const handleHudTimeChange = (e) => {
        dispatch(setHudTimeThreshold(parseInt(e.target.value, 10)));
    };


    const locateOptions = {
        position: 'topright',
        strings: {
            title: t('map.locate_me')
        },
        locateOptions: {
            enableHighAccuracy: true
        },
    };

    return (
        <MapContainer center={currentLocation} zoom={zoom} zoomControl={false}>
            <MapCursor isRoutingMode={isRoutingMode} waypoints={waypoints} />
            <MapCentering center={currentLocation} isDrivingMode={isDrivingMode} />

            <TileLayer
                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                attribution="&copy; <a href=&quot;http://osm.org/copyright&quot;>OpenStreetMap</a> contributors"
            />
            <MapClickHandler onClick={handleMapClick} />

            <TrafficLightMarkers routeCoordinates={routeCoordinates} routeProximity={routeProximity} />
            <TunnelMarkers />

            {isDrivingMode && (
                <UserMarker
                    position={currentLocation}
                    bearing={currentBearing}
                />
            )}

            {isDrivingMode && activeLight && isHudVisible && (
                <TrafficLightHUD activeLight={activeLight} distance={activeLightDistance} />
            )}

            <RoutingMachine
                onRouteFound={handleRouteFound}
                waypoints={isRoutingMode ? waypoints : []}
                isDrivingMode={isDrivingMode}
            />

            <ZoomControl
                zoomInTitle={t('map.zoom_in')}
                zoomOutTitle={t('map.zoom_out')}
                position={"topright"}/>

            <ControlContainer position={'topleft'}>
                {!isDrivingMode && <AppTitle isRoutingMode={isRoutingMode} />}
                {!isDrivingMode && <TrafficInfra />}
                {!isDrivingMode && <TunnelState />}

                <div className={styles.TopLeftControlStack}>
                    <NavigationActionButton
                        isRoutingMode={isRoutingMode}
                        isDrivingMode={isDrivingMode}
                        hasRoute={!!routeCoordinates}
                        onToggleRouting={toggleRoutingMode}
                        onToggleDriving={toggleDrivingMode}
                    />

                    {/* Debug controls - shown when debug mode is enabled (type "varros" to enable, "nysse" to disable) */}
                    <DebugControls
                        routeProximity={routeProximity}
                        onProximityChange={handleProximityChange}
                        hudDistanceThreshold={hudDistanceThreshold}
                        onHudDistanceChange={handleHudDistanceChange}
                        hudTimeThreshold={hudTimeThreshold}
                        onHudTimeChange={handleHudTimeChange}
                        isDrivingMode={isDrivingMode}
                        currentBearing={currentBearing}
                        currentSpeed={currentSpeed}
                        activeLightDistance={activeLightDistance}
                    />
                </div>
            </ControlContainer>

            {/* Top Right Controls Stack */}
            <ControlContainer position={'topright'}>
            </ControlContainer>

            {/* LocateControl usually mounts itself. */}
            {!isDrivingMode && <LocateControl options={locateOptions} />}

            {/* Language Switcher */}
            {!isDrivingMode && (
                <ControlContainer position={'topright'}>
                    <div className="leaflet-bar">
                        <LanguageSwitcher />
                    </div>
                </ControlContainer>
            )}
        </MapContainer>
    );
};

export default MapView;
